/**
 * CSRF Service
 *
 * SECURITY: Manages CSRF tokens for protecting against Cross-Site Request Forgery attacks
 * - Fetches CSRF token from backend
 * - Stores token in memory (not localStorage for security)
 * - Provides token for including in request headers
 * - Handles token refresh when needed
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8008/api';
const CSRF_ENDPOINT = '/auth/csrf-token';

class CsrfService {
  private csrfToken: string | null = null;
  private fetchPromise: Promise<string> | null = null;

  /**
   * Get CSRF token from backend
   * Uses caching to avoid multiple simultaneous requests
   */
  async fetchToken(): Promise<string> {
    // If already fetching, return the existing promise
    if (this.fetchPromise) {
      return this.fetchPromise;
    }

    // Create new fetch promise
    this.fetchPromise = this._fetchTokenFromServer();

    try {
      const token = await this.fetchPromise;
      this.csrfToken = token;
      return token;
    } finally {
      // Clear promise after completion (success or error)
      this.fetchPromise = null;
    }
  }

  /**
   * Internal method to fetch token from server
   */
  private async _fetchTokenFromServer(): Promise<string> {
    try {
      const response = await fetch(`${API_BASE_URL}${CSRF_ENDPOINT}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Important: Send cookies for session
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch CSRF token: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (!data.success || !data.data?.csrf_token) {
        throw new Error('Invalid CSRF token response from server');
      }

      console.log('[CSRF] Token fetched successfully');
      return data.data.csrf_token;
    } catch (error) {
      console.error('[CSRF] Error fetching token:', error);
      throw error;
    }
  }

  /**
   * Get current CSRF token
   * If token doesn't exist, fetches it from server
   */
  async getToken(): Promise<string> {
    if (this.csrfToken) {
      return this.csrfToken;
    }

    return this.fetchToken();
  }

  /**
   * Get current CSRF token synchronously
   * Returns null if not yet fetched
   */
  getTokenSync(): string | null {
    return this.csrfToken;
  }

  /**
   * Refresh CSRF token
   * Forces a new fetch from server
   */
  async refreshToken(): Promise<string> {
    console.log('[CSRF] Refreshing token...');
    this.csrfToken = null;
    this.fetchPromise = null;
    return this.fetchToken();
  }

  /**
   * Clear CSRF token
   * Should be called on logout
   */
  clearToken(): void {
    console.log('[CSRF] Token cleared');
    this.csrfToken = null;
    this.fetchPromise = null;
  }

  /**
   * Check if CSRF token is available
   */
  hasToken(): boolean {
    return this.csrfToken !== null;
  }
}

// Export singleton instance
export const csrfService = new CsrfService();
